/**
 * Google Analytics 4 Integration for MARISSA GOLD AND DIAMONDS
 * Tracks page views, e-commerce events, and user interactions
 */

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

// Initialize GA4
export const initGA = () => {
  if (!GA_MEASUREMENT_ID) {
    return;
  }

  // Add gtag script
  const script1 = document.createElement('script');
  script1.async = true;
  script1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script1);

  // Initialize dataLayer and gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: window.location.pathname,
    currency: 'INR',
    country: 'IN'
  });
};

// Track page views
export const trackPageView = (path, title) => {
  if (!window.gtag || !GA_MEASUREMENT_ID) return;
  
  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: path,
    page_title: title
  });
};

// Track custom events
export const trackEvent = (eventName, params = {}) => {
  if (!window.gtag) return;
  
  window.gtag('event', eventName, params);
};

// E-commerce: View Item
export const trackViewItem = (product) => {
  if (!window.gtag || !product) return;
  
  window.gtag('event', 'view_item', {
    currency: 'INR',
    value: product.total_price || product.base_price || 0,
    items: [{
      item_id: product.id,
      item_name: product.name,
      item_category: product.category,
      item_category2: product.metal_type,
      price: product.total_price || product.base_price || 0,
      quantity: 1
    }]
  });
};

// E-commerce: View Item List (Category page)
export const trackViewItemList = (products, listName) => {
  if (!window.gtag || !products?.length) return;
  
  window.gtag('event', 'view_item_list', {
    item_list_name: listName,
    items: products.slice(0, 10).map((product, index) => ({
      item_id: product.id,
      item_name: product.name,
      item_category: product.category,
      item_category2: product.metal_type,
      price: product.total_price || product.base_price || 0,
      index: index
    }))
  });
};

// E-commerce: Add to Cart
export const trackAddToCart = (product, quantity = 1) => {
  if (!window.gtag || !product) return;
  
  window.gtag('event', 'add_to_cart', {
    currency: 'INR',
    value: (product.total_price || product.base_price || 0) * quantity,
    items: [{
      item_id: product.id,
      item_name: product.name,
      item_category: product.category,
      item_category2: product.metal_type,
      price: product.total_price || product.base_price || 0,
      quantity: quantity
    }]
  });
};

// E-commerce: Remove from Cart
export const trackRemoveFromCart = (product, quantity = 1) => {
  if (!window.gtag || !product) return;
  
  window.gtag('event', 'remove_from_cart', {
    currency: 'INR',
    value: (product.total_price || product.base_price || 0) * quantity,
    items: [{
      item_id: product.id,
      item_name: product.name,
      item_category: product.category,
      price: product.total_price || product.base_price || 0,
      quantity: quantity
    }]
  });
};

// E-commerce: View Cart
export const trackViewCart = (cartItems, totalValue) => {
  if (!window.gtag || !cartItems?.length) return;
  
  window.gtag('event', 'view_cart', {
    currency: 'INR',
    value: totalValue,
    items: cartItems.map(item => ({
      item_id: item.product?.id,
      item_name: item.product?.name,
      item_category: item.product?.category,
      price: item.price?.totalPrice || 0,
      quantity: item.quantity
    }))
  });
};

// E-commerce: Begin Checkout
export const trackBeginCheckout = (cartItems, totalValue) => {
  if (!window.gtag || !cartItems?.length) return;
  
  window.gtag('event', 'begin_checkout', {
    currency: 'INR',
    value: totalValue,
    items: cartItems.map(item => ({
      item_id: item.product?.id,
      item_name: item.product?.name,
      item_category: item.product?.category,
      price: item.price?.totalPrice || 0,
      quantity: item.quantity
    }))
  });
};

// E-commerce: Add Payment Info
export const trackAddPaymentInfo = (paymentMethod, totalValue) => {
  if (!window.gtag) return;
  
  window.gtag('event', 'add_payment_info', {
    currency: 'INR',
    value: totalValue,
    payment_type: paymentMethod
  });
};

// E-commerce: Purchase Complete
export const trackPurchase = (order) => {
  if (!window.gtag || !order) return;
  
  window.gtag('event', 'purchase', {
    transaction_id: order.tracking_id || order.id,
    value: order.total_amount,
    currency: 'INR',
    tax: order.gst_amount || 0,
    shipping: order.shipping_cost || 0,
    items: order.items?.map(item => ({
      item_id: item.product_id,
      item_name: item.product_name,
      price: item.total_price,
      quantity: item.quantity
    })) || []
  });
};

// Track Search
export const trackSearch = (searchTerm, resultsCount) => {
  if (!window.gtag) return;
  
  window.gtag('event', 'search', {
    search_term: searchTerm,
    results_count: resultsCount
  });
};

// Track Login
export const trackLogin = (method = 'email') => {
  if (!window.gtag) return;
  
  window.gtag('event', 'login', {
    method: method
  });
};

// Track Sign Up
export const trackSignUp = (method = 'email') => {
  if (!window.gtag) return;
  
  window.gtag('event', 'sign_up', {
    method: method
  });
};

// Track Add to Wishlist
export const trackAddToWishlist = (product) => {
  if (!window.gtag || !product) return;
  
  window.gtag('event', 'add_to_wishlist', {
    currency: 'INR',
    value: product.total_price || product.base_price || 0,
    items: [{
      item_id: product.id,
      item_name: product.name,
      item_category: product.category,
      price: product.total_price || product.base_price || 0
    }]
  });
};

// Track Share
export const trackShare = (contentType, itemId, method) => {
  if (!window.gtag) return;
  
  window.gtag('event', 'share', {
    content_type: contentType,
    item_id: itemId,
    method: method
  });
};

// Track Contact/Support
export const trackContact = (contactMethod) => {
  if (!window.gtag) return;
  
  window.gtag('event', 'contact', {
    method: contactMethod
  });
};

// Track Filter/Sort Usage
export const trackFilter = (filterType, filterValue) => {
  if (!window.gtag) return;
  
  window.gtag('event', 'filter_products', {
    filter_type: filterType,
    filter_value: filterValue
  });
};

export default {
  initGA,
  trackPageView,
  trackEvent,
  trackViewItem,
  trackViewItemList,
  trackAddToCart,
  trackRemoveFromCart,
  trackViewCart,
  trackBeginCheckout,
  trackAddPaymentInfo,
  trackPurchase,
  trackSearch,
  trackLogin,
  trackSignUp,
  trackAddToWishlist,
  trackShare,
  trackContact,
  trackFilter
};
